import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Step from '../shared/Step';
import RoomUpgradeItem from './RoomUpgradeItem';
import { addProductToCart, removeProductFromCart } from '../../reducers/cart';
import {
  selectRoomUpgradeOption,
  deselectRoomUpgradeOption,
} from '../../reducers/form';
import API from '../../utils/api';
import GTMService from '../../utils/gtm';
import {
  getCampsIntervals,
  joinDates,
  //getDateFromString,
  formatRawDate,
} from '../../utils/helpers';

const api = new API();
const gtm = new GTMService();

const RoomUpgrades = ({
  title,
  gender,
  age,
  selectedCampOptions,
  selectedRoomUpgradeOptions,
  addProductToCart,
  removeProductFromCart,
  selectRoomUpgradeOption,
  deselectRoomUpgradeOption,
}) => {
  const [earlyCheckins, setEarlyCheckins] = useState([]);
  const [privateBoarding, setPrivateBoarding] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let skip = false;

    const intervals = getCampsIntervals(Object.values(selectedCampOptions));

    // Fetch first interval, early checking is first date of stay minus 1 day
    //const earlyCheckinDate = getDateFromString(intervals[0].start);
    const earlyCheckinDate = new Date(new Date(intervals[0].start).toLocaleString('en', {timeZone: 'UTC'}));
    earlyCheckinDate.setDate(earlyCheckinDate.getDate() - 1);
    const earlyCheckinDateFormatted = formatRawDate(earlyCheckinDate);
    console.log('intervals2',earlyCheckinDateFormatted);

    api
      .fetchEarlyCheckin({
        gender,
        age,
        start_date: earlyCheckinDateFormatted,
        end_date: earlyCheckinDateFormatted,
      })
      .then((res) => {
        if (!skip) {
          setEarlyCheckins(res.results);
        }
      });

    Promise.all(
      intervals.map((interval) =>
        api.fetchPrivateBoarding({
          gender,
          age,
          start_date: interval.start,
          end_date: interval.end,
        })
      )
    ).then((res) => {
      if (!skip) {
        setPrivateBoarding(joinDates([].concat(...res.map((r) => r.results))));
      }
    });

    return () => (skip = true);
  }, [gender, age, selectedCampOptions]);

  const handleAddEarlyCheckin = async (item, dateId) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    const product = item.dates.find((d) => d.id === dateId);
    const res = await addProductToCart({
      type: 'room_upgrade',
      product,
    });

    if (res) {
      selectRoomUpgradeOption(product.id, {
        participantProductId: res.participant_product_id,
        name: item.display_name,
        product,
      });
      gtm.trackAddToCart(item, 'gear');
    }
    setIsProcessing(false);
  };

  const handleRemoveEarlyCheckin = async (id) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    await removeProductFromCart(
      selectedRoomUpgradeOptions[id].participantProductId
    );
    deselectRoomUpgradeOption(id);
    setIsProcessing(false);
  };

  const handleAddUpgrades = async (item) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    await Promise.all(
      item.dates.map(async (date) => {
        const res = await addProductToCart({
          type: 'room_upgrade',
          product: date,
        });

        if (res) {
          selectRoomUpgradeOption(date.id, {
            participantProductId: res.participant_product_id,
            name: item.display_name,
            product: date,
          });
          gtm.trackAddToCart(item, 'gear');
        }
      })
    );
    setIsProcessing(false);
  };

  const handleRemoveUpgrades = async (item) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    await Promise.all(
      item.dates.map(async (date) => {
        await removeProductFromCart(
          selectedRoomUpgradeOptions[date.id].participantProductId
        );
        deselectRoomUpgradeOption(date.id);
      })
    );
    setIsProcessing(false);
  };

  if (earlyCheckins.length === 0 && privateBoarding.length === 0) {
    return null;
  }

  return (
    <Step title="+">
      <Step.Sub title={title}>
        <div className="extras">
          <div className="extras__list">
            {earlyCheckins.map((item, index) => {
              const dateId = Object.keys(selectedRoomUpgradeOptions).find(
                (key) => !!item.dates.find((d) => d.id === Number(key))
              );

              return (
                <RoomUpgradeItem
                  key={`${item.name}-${index}`}
                  item={item}
                  startingPrice={item.dates[0].capacity_price}
                  isSelected={!!dateId}
                  onAdd={() => handleAddEarlyCheckin(item, item.dates[0].id)}
                  onRemove={() => handleRemoveEarlyCheckin(dateId)}
                  itemimg="//www.imgacademy.com/sites/default/files/early-checkin.jpg"
                />
              );
            })}

            {privateBoarding.map((item, index) => {
              const isSelected = Object.keys(selectedRoomUpgradeOptions).find(
                (key) => !!item.dates.find((d) => d.id === Number(key))
              );

              return (
                <RoomUpgradeItem
                  key={`${item.name}-${index}`}
                  item={item}
                  // Pricate price is the sum of all weeks
                  startingPrice={item.dates.reduce(
                    (acc, d) => acc + d.capacity_price,
                    0
                  )}
                  isSelected={!!isSelected}
                  onAdd={() => handleAddUpgrades(item)}
                  onRemove={() => handleRemoveUpgrades(item)}
                  itemimg="//www.imgacademy.com/sites/default/files/room-upgrade.jpg"
                />
              );
            })}
          </div>
        </div>
      </Step.Sub>
    </Step>
  );
};

const mapStateToProps = (state) => ({
  gender: state.form.camper.gender,
  age: state.form.camper.age,
  selectedRoomUpgradeOptions: state.form.selectedRoomUpgradeOptions,
  selectedCampOptions: state.form.selectedCampOptions,
});

const mapDispatchToProps = (dispatch) => ({
  addProductToCart: (product) => dispatch(addProductToCart(product)),
  removeProductFromCart: (id) => dispatch(removeProductFromCart(id)),
  selectRoomUpgradeOption: (key, value) =>
    dispatch(selectRoomUpgradeOption(key, value)),
  deselectRoomUpgradeOption: (key) => dispatch(deselectRoomUpgradeOption(key)),
});

RoomUpgrades.propTypes = {
  title: PropTypes.string.isRequired,
  age: PropTypes.string,
  gender: PropTypes.string,
  selectedRoomUpgradeOptions: PropTypes.object.isRequired,
  selectedCampOptions: PropTypes.object.isRequired,
  addProductToCart: PropTypes.func.isRequired,
  removeProductFromCart: PropTypes.func.isRequired,
  selectRoomUpgradeOption: PropTypes.func.isRequired,
  deselectRoomUpgradeOption: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(RoomUpgrades);
